<template>
  <div class="text-decoration">
    <!-- FIXME: Named constants at config level for these attrs -->
    <div class="decoration-category">Concepts</div>
    <div v-if="!$apollo.loading && concepts.length == 0">
      <em>No concept found for the current passage.</em>
    </div>
    <div v-for="concept in concepts" :key="concept.uri" class="list">
      <span
        :class="{ active: conceptIsSelected(concept.uri) }"
        @click.prevent="dynamicallyToggle(concept)"
      >
        {{ conceptLabel(concept) }}
      </span>
      <!-- TODO: Colors with TokenProvider -->
      <span
        v-if="conceptIsSelected(concept.uri)"
        class="decoration-color-legend"
        :style="`background: ${concept.color};`"
      ></span>
    </div>
    <br />
  </div>
</template>

<script>
  import gql from 'graphql-tag';
  import mixins from '@/mixins';

  import { parseOsisRef, convertOsisToUsfm } from '@/common/refUtils';
  import { compareByProperty } from '@/common/sortUtils';
  import { generateRandomHexColor } from '@/common/widgetUtils';
  import {
    DEFAULT_GROUP_KEY,
    RESOLVE_CONCEPTS_TO_PARENT,
    CONCEPTS_ARE_LN,
  } from '../../store/constants';

  const SHOW_CONCEPT_CODE = false;
  const SORT_CONCEPTS_BY_LABEL = true;

  export default {
    name: 'ConceptDecorationWidget',
    mixins: [mixins.InputDataMixin, mixins.OutputsMixin],
    data() {
      return { collapsed: false };
    },
    props: {
      info: Object,
      doric: {
        inputs: {
          label: 'Concept Decoration Widget',
          osisRef: {
            value: 'osisRef',
            groupKey: DEFAULT_GROUP_KEY,
          },
          resolveToParent: RESOLVE_CONCEPTS_TO_PARENT,
        },
        outputs: {
          // FIXME: Distinguish between null and object and some other
          // wrapper
          selectedConcepts: {
            value: {},
            groupKey: DEFAULT_GROUP_KEY,
            send: 'selectedConcepts',
          },
          highlightVerbs: false,
        },
      },
    },
    apollo: {
      concepts: {
        // FIXME: Conditional based on CONCEPTS_ARE_LN
        query: gql`
          query Concepts($filters: ConceptualNodeFilter) {
            conceptualNodes(filters: $filters) {
              id
              label
              uri
              data
            }
          }
        `,
        skip() {
          return !this.contextualizedUsfmRef;
        },
        variables() {
          if (this.shouldResolveToParent) {
            return {
              filters: {
                scriptureReferenceWithAncestors: {
                  usfmRef: this.contextualizedUsfmRef,
                  resolveAncestors: {
                    minDepth: 2,
                  },
                },
                depth: 2,
              },
            };
          }
          return {
            filters: {
              scriptureReferenceWithAncestors: { usfmRef: this.contextualizedUsfmRef },
            },
          };
        },
        update(data) {
          // Should be sorted
          const concepts = data.conceptualNodes.map(concept => {
            let code = SHOW_CONCEPT_CODE ? concept.uri.split('domains:')[1] : undefined;
            if (CONCEPTS_ARE_LN) {
              code = concept.data?.code || undefined;
            }
            return {
              ...concept,
              code,
              color: generateRandomHexColor(),
            };
          });
          if (SORT_CONCEPTS_BY_LABEL) {
            return concepts.sort(compareByProperty('label'));
          }
          return concepts;
        },
      },
    },
    computed: {
      selectedConcepts() {
        return this.outputs.selectedConcepts.value;
      },
      parsedOsisRef() {
        return parseOsisRef(this.osisRef);
      },
      contextualizedOsisRef() {
        return `${this.parsedOsisRef.book}.${this.parsedOsisRef.chapter ?? 1}`;
      },
      contextualizedUsfmRef() {
        return this.osisRef ? convertOsisToUsfm(this.contextualizedOsisRef) : null;
      },
      shouldResolveToParent() {
        // FIXME: Doric types!
        const value = this.resolveToParent;
        return value ? value.toString() === 'true' : false;
      },
    },
    methods: {
      conceptIsSelected(conceptUri) {
        return this.selectedConcepts?.[conceptUri]?.selected || false;
      },
      dynamicallyToggle(concept) {
        this.outputs.selectedConcepts.value[concept.uri] = {
          ...concept,
          selected: !this.conceptIsSelected(concept.uri),
        };
        this.submit();
      },
      conceptLabel(concept) {
        const label = concept.label || '<Unknown>';
        return SHOW_CONCEPT_CODE ? `${concept.code} : ${label}` : label;
      },
    },
  };
</script>

<style scoped lang="scss">
  span {
    cursor: pointer;
    &.active {
      font-weight: bold;
    }
  }

  .list {
    margin-left: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .decoration-category {
    margin-bottom: 0.5rem;
    cursor: pointer;
  }

  .decoration-color-legend {
    display: inline-block;
    width: 0.7rem;
    height: 0.7rem;
    margin-left: 0.2rem;
  }
</style>
